import { Button, Checkbox, Col, DatePicker, Form, Input, Modal, Row, Select, Spin, Upload } from "antd";
import axios from "axios";
import { message } from "components/Messages/Messages";
import { uploadButton } from "components/UploadButton/UploadButton";
import { getBase64 } from "helpers/functions";
import { Member, StudentUser } from "interfaces/users";
import moment from "moment";
import { useEffect, useState } from "react";

const { Option } = Select;

interface Props {
  existingMembers: Member[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (NewMember: Member) => void;
}

const SelectNewMember: React.FC<Props> = ({ isOpen, onClose, onSave, existingMembers }) => {
  const [data, setData] = useState<StudentUser[]>();
  const [selectedStudent, setSelectedStudent] = useState<StudentUser>();
  const [loadingStudents, setLoadingStudents] = useState<boolean>(true);
  const [loadingAddMember, setLoadingAddMember] = useState<boolean>(false);

  const [form] = Form.useForm();

  const [file, setFile] = useState<File | null>(null);

  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);

  const [searchValue, setSearchValue] = useState<string>("");

  const [timeoutId, setTimeoutId] = useState<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!selectedStudent) return;
    const emailPrefix = selectedStudent.last_name
      .trim()
      .split(" ")
      .slice(-1)[0]
      .toLowerCase()
      .replace("ø", "o")
      .replace("æ", "ae")
      .replace("å", "a");
    form.setFieldsValue({
      full_name: `${selectedStudent?.first_name.trim()} ${selectedStudent?.last_name.trim()}`,
      bl_email: `${emailPrefix}@bindeleddet.no`,
      mobile: selectedStudent?.mobile_number,
      seniority: options[0]?.value,
      is_active_member: true,
      show_on_bl_page: true,
    });
  }, [form, selectedStudent, options]);

  useEffect(
    function fetchStudents() {
      setLoadingStudents(true);
      const params = new URLSearchParams({
        has_graduated: "false",
      });

      if (searchValue) {
        params.append("search", searchValue);
      }

      axios
        .get<{ results: StudentUser[] }>(`/students/?${params.toString()}`)
        .then(response => {
          setData(response.data.results.filter(student => existingMembers.every(member => member.id !== student.id)));
          setLoadingStudents(false);
        })
        .catch(() => {
          setLoadingStudents(false);
        });
    },
    [existingMembers, searchValue]
  );

  const handleSearch = (value: string) => {
    // Debounce search to prevent too many requests
    setLoadingStudents(true);
    setData([]);
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    const newTimeoutId = setTimeout(() => {
      setSearchValue(value);
    }, 300);
    setTimeoutId(newTimeoutId);
  };

  const AddMember = () => {
    if (!selectedStudent) return;

    setLoadingAddMember(true);

    const { birthday, ...otherValues } = form.getFieldsValue();

    const formData = new FormData();

    // Bare send student_id og BL-spesifikk informasjon
    formData.append("id", String(selectedStudent.id));

    Object.entries(otherValues).forEach(([key, value]) => {
      if (!value) return;
      formData.append(key, String(value));
    });

    if (birthday) {
      const formattedBirthday = moment(birthday).format("YYYY-MM-DD");
      formData.append("birthday", formattedBirthday);
    }

    if (file) {
      formData.append("profile_picture", file);
    }

    axios
      .post<Member>("/internal/members/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(response => {
        onSave(response.data);
        onClose();
        setLoadingAddMember(false);
      })
      .catch(() => {
        message("error", "Feil under lagring av nytt medlem");
        setLoadingAddMember(false);
      });
  };

  const handleStudentChange = (studentId: number) => {
    const student = data?.find(s => s.id === studentId);
    setSelectedStudent(student);
  };

  useEffect(() => {
    function generateOptions() {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;

      const temporaryOptions: React.SetStateAction<{ value: string; label: string }[]> = [];

      for (let i = 0; i < 5; i += 1) {
        if (currentMonth >= 8) {
          // If august or later, open for being a member of the current year
          const optionValue = `Høst ${(currentYear - i) % 100}`;
          temporaryOptions.push({ value: optionValue, label: optionValue });
        } else {
          /*     const year = currentYear - i - 1; */
          const optionValue = `Høst ${(currentYear - i - 1) % 100}`;
          temporaryOptions.push({ value: optionValue, label: optionValue });
        }
      }
      setOptions(temporaryOptions);
    }
    generateOptions();
  }, []);

  const [preview, setPreview] = useState<string | null>(null);

  useEffect(
    function setPreviewImage() {
      if (file) {
        getBase64(file)
          .then((result: string | null) => {
            setPreview(result);
          })
          .catch(() => {
            setPreview(null);
          });
      }
    },
    [file]
  );

  return (
    <Modal
      title="Velg student som skal registreres som BL-medlem"
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Avbryt
        </Button>,
        <Button key="submit" type="primary" disabled={!selectedStudent} onClick={AddMember} loading={loadingAddMember}>
          Lagre nytt medlem
        </Button>,
      ]}
    >
      <Row gutter={[30, 30]}>
        <Col span={24}>
          <Form.Item<StudentUser> name="is_open">
            <Select
              onChange={handleStudentChange}
              size="large"
              style={{ width: "100%" }}
              placeholder="Søk etter studentens navn eller brukernavn"
              notFoundContent={
                loadingStudents ? (
                  <div style={{ textAlign: "center" }}>
                    <Spin size="default" />
                  </div>
                ) : (
                  "Fant ingen studenter"
                )
              }
              showSearch
              onSearch={handleSearch}
              filterOption={false}
            >
              {data?.map(student => (
                <Option key={student.id} value={student.id}>
                  {`${student.first_name.trim()} ${student.last_name.trim()} (${student.username})`}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      {selectedStudent && (
        <Form form={form} layout="vertical" requiredMark>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Navn" name="full_name">
                <Input placeholder="Skriv inn navn..." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Bindeleddetmail" name="bl_email">
                <Input placeholder="Skriv inn BL-mail..." />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Mobilnummer" name="mobile">
                <Input placeholder="Skriv inn BL-mail..." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Bursdag" name="birthday">
                <DatePicker style={{ width: "100%" }} placeholder="dd.mm.åååå" format="DD.MM.yyyy" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Medlem siden" name="seniority">
                <Select options={options} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Verv" name="duties">
                <Input placeholder="Skriv inn lederverv..." />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={12}>
              <Form.Item label="Profilbilde">
                <Upload
                  listType="picture-card"
                  className="avatar-uploader"
                  maxCount={2}
                  beforeUpload={f => {
                    setFile(f);

                    // Return false to prevent immediate upload
                    return false;
                  }}
                  onRemove={() => {
                    setFile(null);
                    setPreview(null);
                  }}
                  fileList={
                    file
                      ? preview
                        ? [
                            {
                              uid: "-1",
                              name: "New profile picture",
                              url: preview,
                            },
                          ]
                        : []
                      : []
                  }
                >
                  {uploadButton}
                </Upload>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Aktivt medlem" name="is_active_member" valuePropName="checked">
                <Checkbox style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Vis på nettsiden" name="show_on_bl_page" valuePropName="checked">
                <Checkbox />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}
    </Modal>
  );
};

export default SelectNewMember;
